import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import logoImage from '../assets/acts-full-logo.svg';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { trpc } from '../lib/trpc';
import { useAuthStore, setToken } from '@repo/api-client';

const Verification = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const email = params.get('email');
  const phone = params.get('phone');
  const [currentRef, setCurrentRef] = useState(params.get('ref') || '');
  const [urlOtp, setUrlOtp] = useState(params.get('otp') || '');
  const isMobile = !!phone || params.get('type') === 'mobile';

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<{ code: string }>();

  // TRPC mutations
  const verifyEmailMutation = trpc.user.verifyEmail.useMutation();
  const resendEmailMutation = trpc.user.resendVerificationCode.useMutation();
  const verifyMobileMutation = trpc.user.verifyMobile.useMutation();
  const resendMobileMutation = trpc.user.resendMobileVerificationCode.useMutation();

  // Query recent mock SMS for dev convenience
  const { data: mockSmsData, refetch: refetchMockSms } = trpc.user.getRecentMockSms.useQuery(
    { phone: phone || undefined, ref: currentRef || undefined },
    { enabled: isMobile && (import.meta.env.DEV || !import.meta.env.PROD) }
  );

  const displayOtp = mockSmsData?.code || urlOtp;

  const onSubmit = async (data: { code: string }) => {
    setIsLoading(true);
    setError('');

    if (isMobile) {
      if (!phone) {
        setError(t('verification.errors.verificationFailed'));
        setIsLoading(false);
        return;
      }

      try {
        const result = await verifyMobileMutation.mutateAsync({
          phone,
          code: data.code,
          ref: currentRef || undefined,
        });

        if (result?.success) {
          setSuccess(true);
          if (result.token && result.user) {
            login(result.user, result.token);
            setToken(result.token);
          }
          setTimeout(() => {
            if (
              window.location.hostname === 'auth.jabeztech.com' ||
              (window.location.hostname === 'localhost' && window.location.port === '4001')
            ) {
              navigate('/profile');
            } else {
              navigate('/');
            }
          }, 1500);
        } else {
          if (result.remainingAttempts !== undefined) {
            setError(
              t('verification.errors.attemptsRemaining', {
                remaining: result.remainingAttempts,
              })
            );
          } else {
            setError(t('verification.errors.invalidCode'));
          }
        }
      } catch (err: any) {
        console.error('Mobile verification error', err);
        setError(
          err.message ? t(err.message) : t('verification.errors.verificationFailed')
        );
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Email verification path
    if (!email) {
      setError(t('verification.errors.verificationFailed'));
      setIsLoading(false);
      return;
    }

    try {
      const result = await verifyEmailMutation.mutateAsync({
        email,
        code: data.code,
      });

      if (result?.success) {
        setSuccess(true);
        setTimeout(() => {
          if (
            window.location.hostname === 'auth.jabeztech.com' ||
            (window.location.hostname === 'localhost' && window.location.port === '4001')
          ) {
            navigate('/profile');
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setError(t('verification.errors.invalidCode'));
      }
    } catch (err: any) {
      console.error('Verification error', err);
      setError(
        err.message ? t(err.message) : t('verification.errors.verificationFailed')
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      if (isMobile) {
        if (!phone) {
          setError(t('verification.errors.verificationFailed'));
          return;
        }
        const res = await resendMobileMutation.mutateAsync({ phone });
        if (res?.ref) {
          setCurrentRef(res.ref);
        }
        if ((res as any)?.code) {
          setUrlOtp((res as any).code);
        }
        refetchMockSms();
        alert(t('verification.messages.codeResent'));
      } else {
        if (!email) {
          setError(t('verification.errors.verificationFailed'));
          return;
        }
        await resendEmailMutation.mutateAsync({ email });
        alert(t('verification.messages.codeResent'));
      }
    } catch (err: any) {
      console.error('Resend error', err);
      if (err.message?.startsWith('verification.errors.cooldown:')) {
        const secs = err.message.split(':')[1];
        setError(t('verification.errors.cooldown', { seconds: secs }));
      } else {
        setError(
          err.message ? t(err.message) : t('verification.errors.verificationFailed')
        );
      }
    }
  };

  return (
    <div className={`md:!bg-blue-200`}>
      <div
        className={`flex h-screen flex-col items-center justify-center overflow-auto`}
      >
        <div className='w-full max-w-md bg-white p-6 md:shadow-md md:rounded-lg'>
          <img
            src={logoImage}
            alt='Acts Camp Logo'
            className='w-full h-35 md:h-40'
          />
          <div className='flex justify-between items-center align-middle pt-2 pb-4'>
            <div />
            <h2 className='text-2xl font-bold text-center'>
              {isMobile ? t('verification.mobileTitle') : t('verification.title')}
            </h2>
            <div className='flex justify-end'>
              <LanguageSwitcher />
            </div>
          </div>

          {isMobile && currentRef && (
            <div className='mb-4 py-2 px-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between'>
              <div className='text-xs text-blue-700'>
                <span className='font-medium'>{t('verification.referenceCode') || 'SMS Reference Code'}:</span>
                <span className='block text-[11px] text-blue-500 mt-0.5'>{t('verification.referenceNotice') || 'Match reference on incoming SMS'}</span>
              </div>
              <div className='flex items-center gap-2'>
                {displayOtp && (
                  <button
                    type='button'
                    onClick={() => setValue('code', displayOtp)}
                    className='font-mono text-xs bg-amber-100 text-amber-900 border border-amber-300 px-2 py-1 rounded hover:bg-amber-200 transition-colors cursor-pointer font-bold'
                    title='Click to autofill OTP'
                  >
                    OTP: {displayOtp}
                  </button>
                )}
                <span className='font-mono font-bold text-sm text-blue-900 bg-white px-2.5 py-1 rounded border border-blue-200 shadow-sm'>
                  Ref: {currentRef}
                </span>
              </div>
            </div>
          )}

          {displayOtp && (
            <div className='mb-4 p-3 bg-amber-50 border border-amber-300 rounded-md text-sm text-amber-900'>
              <div className='flex items-center justify-between'>
                <span className='font-semibold text-xs uppercase tracking-wide bg-amber-200 text-amber-800 px-2 py-0.5 rounded'>
                  Dev Mock SMS
                </span>
                <span className='text-xs text-amber-600'>For testing</span>
              </div>
              <p className='mt-1 text-xs'>
                Latest OTP for <strong>{mockSmsData?.to || phone}</strong>:{' '}
                <button
                  type='button'
                  onClick={() => setValue('code', displayOtp)}
                  className='font-mono font-bold text-base text-blue-700 underline hover:text-blue-900 ml-1 cursor-pointer'
                  title='Click to autofill code'
                >
                  {displayOtp}
                </button>
                {(mockSmsData?.ref || currentRef) && (
                  <span className='ml-2 font-mono text-xs text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded'>
                    Ref: {mockSmsData?.ref || currentRef}
                  </span>
                )}
              </p>
            </div>
          )}

          {success ? (
            <div className='mb-4 text-sm text-green-600 text-center'>
              {t('verification.successMessage')}
            </div>
          ) : (
            <>
              <p className='text-sm text-gray-600 mb-6 text-center'>
                {isMobile
                  ? t('verification.mobileInstructions', { phone: phone || '' })
                  : t('verification.instructions')}
              </p>

              {error && (
                <div className='mb-4 text-sm text-red-600 text-center'>
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
                <div>
                  <label
                    htmlFor='code'
                    className='block text-sm font-medium text-gray-700'
                  >
                    {t('verification.fields.code')}
                  </label>
                  <input
                    id='code'
                    type='text'
                    maxLength={6}
                    {...register('code', {
                      required: t('verification.errors.codeRequired'),
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: t('verification.errors.invalidCodeFormat'),
                      },
                    })}
                    className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 ${errors.code
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-blue-500'
                      }`}
                    placeholder={t('verification.placeholders.code')}
                  />
                  {errors.code && (
                    <p className='mt-1 text-sm text-red-600'>
                      {errors.code.message}
                    </p>
                  )}
                </div>

                <button
                  type='submit'
                  disabled={isLoading}
                  className='w-full rounded-md bg-blue-600 py-2 px-4 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50'
                >
                  {isLoading
                    ? t('verification.buttons.verifying')
                    : isMobile
                    ? t('verification.buttons.verifyMobile')
                    : t('verification.buttons.verify')}
                </button>
              </form>

              <div className='mt-6 text-center'>
                <button
                  onClick={handleResend}
                  className='text-sm font-medium text-blue-600 hover:text-blue-500'
                >
                  {t('verification.buttons.resendCode')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Verification;
